import React, { Fragment, useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
    Container,
    Card,
    CardBody,
    CardFooter,
    Form,
    Button,
    Label,
    Input,
    FormGroup,
} from "reactstrap";
import { ToastContainer, toast } from "react-toastify";
import Breadcrumbs from "../../Components/Common/Breadcrumb";
import AxiosInstance from "../../services/AxiosInstance";
import "react-toastify/dist/ReactToastify.css";

const OPERATION_OPTIONS = ["access", "add", "edit", "delete"];

const operationArrayToString = (arr) =>
    (Array.isArray(arr) ? arr.filter(Boolean) : []).join("|") || "access";

const operationStringToArray = (str) => {
    if (!str || typeof str !== "string") return [];
    return str.split("|").map((s) => s.trim()).filter(Boolean);
};

const AddModulePage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const editingModule = location.state?.editingModule;

    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState({
        module_name: "",
        controller_name: "",
        fa_icon: "",
        operation: ["access"],
        sort_order: 0,
    });
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (editingModule) {
            setForm({
                module_name: editingModule.module_name || "",
                controller_name: editingModule.controller_name || "",
                fa_icon: editingModule.fa_icon || "",
                operation: operationStringToArray(editingModule.operation).length ? operationStringToArray(editingModule.operation) : ["access"],
                sort_order: Number(editingModule.sort_order) || 0,
            });
        }
    }, [editingModule]);

    const handleOperationChange = (op, checked) => {
        setForm((prev) => {
            const arr = checked ? [...(prev.operation || []), op] : (prev.operation || []).filter((o) => o !== op);
            if (arr.length === 0) return { ...prev, operation: ["access"] };
            return { ...prev, operation: arr };
        });
        if (errors.operation) setErrors((prev) => ({ ...prev, operation: "" }));
    };

    const validate = () => {
        const err = {};
        if (!form.module_name?.trim()) err.module_name = "Module name is required";
        if (!form.controller_name?.trim()) err.controller_name = "Controller name is required";
        setErrors(err);
        return Object.keys(err).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;
        setSaving(true);
        try {
            const payload = {
                module_name: form.module_name.trim(),
                controller_name: form.controller_name.trim(),
                fa_icon: form.fa_icon.trim(),
                operation: operationArrayToString(form.operation),
                sort_order: form.sort_order,
            };
            if (editingModule?.module_id) {
                const res = await AxiosInstance().post(`/admin/module/update/${editingModule.module_id}`, payload);
                if (res.data.status) {
                    toast.success(res.data.message || "Module updated.");
                    navigate("/job/admin/manage-modules");
                } else {
                    toast.error(res.data.message || "Update failed.");
                }
            } else {
                const res = await AxiosInstance().post("/admin/module/create", payload);
                if (res.data.status) {
                    toast.success(res.data.message || "Module created.");
                    navigate("/job/admin/manage-modules");
                } else {
                    toast.error(res.data.message || "Create failed.");
                }
            }
        } catch (e) {
            toast.error(e.response?.data?.message || "Request failed.");
        } finally {
            setSaving(false);
        }
    };

    const goBack = () => navigate("/job/admin/manage-modules");

    return (
        <Fragment>
            <Breadcrumbs title={editingModule ? "Edit Module" : "Add Module"} mainTitle="Module Management" />
            <Container fluid={true}>
                <Form onSubmit={handleSubmit} className="theme-form">
                    <Card>
                        <CardBody>
                            <h5 className="mb-4">Module Details</h5>
                            <FormGroup className="mb-3">
                                <Label className="col-form-label m-0 pt-0">
                                    Module Name<span className="text-danger">*</span>
                                </Label>
                                <Input
                                    className="form-control form-control-sm"
                                    type="text"
                                    value={form.module_name}
                                    onChange={(e) => {
                                        setForm((p) => ({ ...p, module_name: e.target.value }));
                                        if (errors.module_name) setErrors((prev) => ({ ...prev, module_name: "" }));
                                    }}
                                    placeholder="e.g. Manage Reports"
                                    invalid={!!errors.module_name}
                                />
                                {errors.module_name && (
                                    <small className="text-danger">{errors.module_name}</small>
                                )}
                            </FormGroup>
                            <FormGroup className="mb-3">
                                <Label>Controller Name<span className="text-danger">*</span></Label>
                                <Input
                                    className="form-control form-control-sm"
                                    type="text"
                                    value={form.controller_name}
                                    onChange={(e) => {
                                        setForm((p) => ({ ...p, controller_name: e.target.value }));
                                        if (errors.controller_name) setErrors((prev) => ({ ...prev, controller_name: "" }));
                                    }}
                                    placeholder="e.g. report"
                                    invalid={!!errors.controller_name}
                                />
                                <small className="text-muted">Unique identifier; used in permission matrix and sidebar.</small>
                                {errors.controller_name && (
                                    <small className="text-danger d-block">{errors.controller_name}</small>
                                )}
                            </FormGroup>
                            <FormGroup className="mb-3">
                                <Label>FontAwesome Icon</Label>
                                <Input
                                    className="form-control form-control-sm"
                                    type="text"
                                    value={form.fa_icon}
                                    onChange={(e) => setForm((p) => ({ ...p, fa_icon: e.target.value }))}
                                    placeholder="e.g. fa-chart-bar"
                                />
                            </FormGroup>
                            <FormGroup className="mb-3">
                                <Label>Operations</Label>
                                <div className="d-flex flex-wrap gap-3 align-items-center">
                                    {OPERATION_OPTIONS.map((op) => (
                                        <div key={op} className="form-check form-check-inline mb-0">
                                            <Input
                                                className="checkbox_animated mr-2"
                                                type="checkbox"
                                                id={`chk-op-${op}`}
                                                checked={(form.operation || []).includes(op)}
                                                onChange={(e) => handleOperationChange(op, e.target.checked)}
                                            />
                                            <Label className="form-check-label text-capitalize ms-2 mb-0" htmlFor={`chk-op-${op}`}>
                                                {op}
                                            </Label>
                                        </div>
                                    ))}
                                </div>
                                <small className="text-muted d-block mt-1">Operations available for this module in the Role Permission matrix.</small>
                            </FormGroup>
                            <FormGroup className="mb-3">
                                <Label>Sort Order</Label>
                                <Input
                                    className="form-control form-control-sm"
                                    type="number"
                                    min={0}
                                    value={form.sort_order}
                                    onChange={(e) => setForm((p) => ({ ...p, sort_order: parseInt(e.target.value, 10) || 0 }))}
                                />
                            </FormGroup>
                        </CardBody>
                        <CardFooter className="text-end">
                            <Button type="button" color="secondary" className="me-2" onClick={goBack}>
                                Cancel
                            </Button>
                            <Button color="primary" type="submit" disabled={saving}>
                                {saving ? (editingModule ? "Updating..." : "Creating...") : (editingModule ? "Update Module" : "Create Module")}
                            </Button>
                        </CardFooter>
                    </Card>
                </Form>
            </Container>
            <ToastContainer />
        </Fragment>
    );
};

export default AddModulePage;
